import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { environment } from '../../environments/environment';

export interface OwnerAuth {
  'access-token': string;
  client: string;
  uid: string;
  'token-type'?: string;
  expiry?: string;
}

export interface Owner {
  id: number;
  name: string;
  email: string;
  whatsapp: string;
  google: boolean;
  cpf?: string;
}

export interface SignupLead {
  name: string;
  email: string;
  whatsapp: string;
  expires_at: string;
  account: boolean;
  chosen_plan: string | null;
}

export interface Plan {
  key: string;
  name: string;
  price: number;
  monthly_orders_limit: number | null;
  monthly_menu_views_limit: number | null;
  monthly_reservation_views_limit: number | null;
  online_orders_site: boolean;
  evaluations_site: boolean;
  selfservice: boolean;
  whatsapp_confirmations: boolean;
  priority_support: boolean;
}

export interface SignupCompany {
  name?: string;
  nome_fantasia: string;
  razao_social: string;
  cnpj: string;
  address: string;
  district: string;
  city: string;
  computer_os: string;
  plan?: string | null;
}

export interface SignupProgress {
  owner: Owner;
  company: SignupCompany | null;
  steps: { company: boolean; plan: boolean; personal: boolean };
  chosen_plan: string | null;
  plans: Plan[];
  contract: { version: string; text: string };
  completed: { name: string; nome_fantasia: string; plan: string }[];
}

export interface CustomerCompany {
  name: string;
  nome_fantasia: string;
  plan: string | null;
  installed: boolean;
  plan_paid_until: string | null;
  payment_url: string | null;
  card_autopay: boolean;
  payment: 'waiting_install' | 'open' | 'overdue' | null;
}

export interface CustomerArea {
  owner: Owner;
  plans: Plan[];
  companies: CustomerCompany[];
  signup_in_progress: boolean;
}

export interface SignupResult {
  company: { name: string; nome_fantasia: string };
  plan: string;
  payment_pending: boolean;
  install_code: string;
  expires_at: string;
}

const AUTH_KEY = 'ncommerce_owner_auth';

// Talks to the master (ncommerce_server_api) for the self-service signup and
// the Área do Cliente.
// The owner's Devise Token Auth headers stay in localStorage so the signup
// survives a reload; a blocked storage only means signing in again.
@Injectable({
  providedIn: 'root'
})
export class SignupApiService {
  private url = environment.server_url;

  constructor(private http: HttpClient) { }

  requestLink(data: { name: string; email: string; whatsapp: string; source?: string; chosen_plan?: string },
              turnstileToken: string) {
    return this.http.post<{ sent: boolean; email: string }>(`${this.url}/signup/leads`,
      { ...data, cf_turnstile_response: turnstileToken });
  }

  lead(token: string) {
    return this.http.get<SignupLead>(`${this.url}/signup/${encodeURIComponent(token)}`);
  }

  createAccount(token: string, body: Record<string, string>): Observable<{ owner: Owner; auth: OwnerAuth }> {
    return this.http.post<{ owner: Owner; auth: OwnerAuth }>(`${this.url}/signup/${encodeURIComponent(token)}/account`, body)
      .pipe(tap(res => this.saveAuth(res.auth)));
  }

  signIn(email: string, password: string): Observable<HttpResponse<unknown>> {
    return this.http.post<unknown>(`${this.url}/owner_auth/sign_in`, { email, password }, { observe: 'response' })
      .pipe(tap(res => this.saveAuth({
        'access-token': res.headers.get('access-token') || '',
        client: res.headers.get('client') || '',
        uid: res.headers.get('uid') || '',
        'token-type': res.headers.get('token-type') || 'Bearer',
        expiry: res.headers.get('expiry') || ''
      })));
  }

  googleSignIn(credential: string): Observable<{ owner: Owner; auth: OwnerAuth }> {
    return this.http.post<{ owner: Owner; auth: OwnerAuth }>(`${this.url}/owners/google_sign_in`,
      { google_credential: credential }).pipe(tap(res => this.saveAuth(res.auth)));
  }

  progress() {
    return this.http.get<SignupProgress>(`${this.url}/owners/signup`, { headers: this.authHeaders() });
  }

  saveStep(step: 'company' | 'plan' | 'personal', body: object) {
    return this.http.post<SignupProgress>(`${this.url}/owners/signup/${step}`, body, { headers: this.authHeaders() });
  }

  accept(contractVersion: string, turnstileToken: string) {
    return this.http.post<SignupResult>(`${this.url}/owners/signup/accept`,
      { contract_version: contractVersion, cf_turnstile_response: turnstileToken }, { headers: this.authHeaders() });
  }

  customerArea() {
    return this.http.get<CustomerArea>(`${this.url}/owners/companies`, { headers: this.authHeaders() });
  }

  changePlan(companyName: string, plan: string) {
    return this.http.post<CustomerArea>(`${this.url}/owners/companies/${encodeURIComponent(companyName)}/plan`,
      { plan }, { headers: this.authHeaders() });
  }

  cardCheckout(companyName: string) {
    return this.http.post<{ link: string }>(`${this.url}/owners/companies/${encodeURIComponent(companyName)}/card`,
      {}, { headers: this.authHeaders() });
  }

  downloadUrl(code: string): string {
    return `${this.url}/install/${code}/download`;
  }

  hasAuth(): boolean {
    return !!this.auth();
  }

  signOut(): void {
    try {
      localStorage.removeItem(AUTH_KEY);
    } catch (e) { }
    this.memoryAuth = null;
  }

  private memoryAuth: OwnerAuth | null = null;

  private auth(): OwnerAuth | null {
    if (this.memoryAuth) {
      return this.memoryAuth;
    }
    try {
      const raw = localStorage.getItem(AUTH_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  private saveAuth(auth: OwnerAuth): void {
    if (!auth || !auth['access-token']) {
      return;
    }
    this.memoryAuth = auth;
    try {
      localStorage.setItem(AUTH_KEY, JSON.stringify(auth));
    } catch (e) { }
  }

  private authHeaders(): HttpHeaders {
    const auth = this.auth();
    let headers = new HttpHeaders();
    if (auth) {
      headers = headers.set('access-token', auth['access-token']).set('client', auth.client)
        .set('uid', auth.uid).set('token-type', auth['token-type'] || 'Bearer');
      if (auth.expiry) {
        headers = headers.set('expiry', auth.expiry);
      }
    }
    return headers;
  }
}

export function apiError(err: any, fallback = 'Não foi possível concluir agora. Tente de novo em alguns minutos.'): string {
  const errors = err && err.error && err.error.errors;
  if (Array.isArray(errors) && errors.length) {
    return errors.join(' ');
  }
  if (errors && typeof errors === 'object' && Array.isArray(errors.full_messages)) {
    return errors.full_messages.join(' ');
  }
  return fallback;
}
